import { useMutation } from '@apollo/client';
import { useMelwaysInfo } from '@seek/melways-react';
import { metrics } from '@seek/metrics-js';
import { useTranslations } from '@vocab/react';
import React, { useEffect } from 'react';

import { browserLogError } from 'src/modules/error-logger/browserLog';
import { updateSubscriptionQL } from 'src/modules/graphql/notifications/preference.graphql';
import { useTypedSelector } from 'src/reducers';
import type {
  UpdateNotificationSubscriptionWithTokenMutation,
  UpdateNotificationSubscriptionWithTokenMutationVariables,
} from 'src/types/graphql';

import Subscription from '../common/subscription';
import PageLoader from '../page-loader';
import { trackSubscribeSuccessPageLoad } from '../tracking/tealium';

import translations from './.vocab';
import resubscribeIcon from './img/resubscribe-icon.png';
import SubscribeError from './subscribe-error';

const Subscribe = ({ token }: { token: string }) => {
  const { language } = useMelwaysInfo();
  const [subscribeMutation, { error, data }] = useMutation<
    UpdateNotificationSubscriptionWithTokenMutation,
    UpdateNotificationSubscriptionWithTokenMutationVariables
  >(updateSubscriptionQL);

  const locale = useTypedSelector(
    (state) => state.page.pageAnalytics.siteCountry,
  );
  const zone = useTypedSelector((store) => store.page.pageAnalytics.zone);
  const { t } = useTranslations(translations);

  useEffect(() => {
    if (token) {
      subscribeMutation({
        variables: {
          input: {
            token,
            subscribe: true,
            locale: `${language}-${locale.toUpperCase()}`,
          },
        },
      }).catch((e) => browserLogError(e));
    }
  }, [subscribeMutation, token, language, locale]);

  useEffect(() => {
    if (error) {
      metrics.count('subscribe-error-page-rendered');
    } else {
      metrics.count('subscribe-page-rendered');
    }
  }, [error]);

  if (error) {
    return <SubscribeError language={language} zone={zone} />;
  }

  if (data) {
    const result = data.updateNotificationSubscriptionWithToken;
    const product = result?.product.code;
    const channel = result?.subscriptions[0].channel.code; // always have 1
    trackSubscribeSuccessPageLoad(product, channel);

    return (
      <Subscription
        title={t('subscribe-subscription-title')}
        cardTitle={t('subscribe-subscription-card-title')}
        body={t('subscribe-subscription-body')}
        linkMessage={t('subscribe-subscription-link-message')}
        icon={resubscribeIcon}
      />
    );
  }

  return <PageLoader />;
};

export default Subscribe;
